import { useEffect, useState, useRef } from 'react'
import { createRoot } from 'react-dom/client'
import { MapContainer, TileLayer, GeoJSON, Popup, CircleMarker, Pane, Marker } from 'react-leaflet'
import { useMap } from 'react-leaflet'
import L, { LatLngExpression } from 'leaflet'
import 'leaflet.geodesic'
import 'leaflet/dist/leaflet.css'
import { DataService } from '@/lib/data-service'
import { CountryDistance, CountryData, formatDistance, bearingToCardinal, distanceToWarmthText, haversineDistance, initialBearing } from '@/lib/utils'
import { cn } from '@/lib/utils'

interface WorldMapProps {
  className?: string
}

// Custom Geodesic component using leaflet.geodesic
function GeodesicLine({ positions, pathOptions, children }: {
  positions: LatLngExpression[]
  pathOptions?: L.PolylineOptions
  children?: React.ReactNode
}) {
  const map = useMap()
  const geodesicRef = useRef<L.Geodesic>(null)

  useEffect(() => {
    if (!map || !positions || positions.length < 2) return

    // Remove existing geodesic if it exists
    if (geodesicRef.current) {
      map.removeLayer(geodesicRef.current as unknown as L.Layer)
    }

    // Create new geodesic line with proper options
    const geodesicOptions: L.GeodesicOptions = {
      color: pathOptions?.color || '#7f03fc',
      weight: pathOptions?.weight || 2,
      opacity: pathOptions?.opacity || 0.9,
      dashArray: pathOptions?.dashArray || '5, 10',
      steps: 50,
      wrap: true
    }
    
    const geodesic = L.geodesic(positions, geodesicOptions)
    
    geodesic.addTo(map)
    geodesicRef.current = geodesic

    // Add popup if children provided
    if (children) {
      const popupContent = document.createElement('div')
      const root = createRoot(popupContent)
      root.render(children as React.ReactElement)
      geodesic.bindPopup(popupContent)
    }

    return () => {
      if (geodesicRef.current && map.hasLayer(geodesicRef.current as unknown as L.Layer)) {
        map.removeLayer(geodesicRef.current as unknown as L.Layer)
      }
    }
  }, [map, positions, pathOptions, children])

  return null
}

export function WorldMap({ className }: WorldMapProps) {
  const [countryData, setCountryData] = useState<CountryData | null>(null)
  const [selectedCountries, setSelectedCountries] = useState<string[]>([])
  const selectCountriesRef = useRef<string[]>([])
  const [distanceInfo, setDistanceInfo] = useState<CountryDistance | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [territories, setTerritories] = useState<any[]>([])
  const [distanceMode, setDistanceMode] = useState<'v1' | 'v2'>('v2')
  const dataService = useRef(DataService.getInstance())

  useEffect(() => {
    selectCountriesRef.current = selectedCountries
  }, [selectedCountries])

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true)
        await dataService.current.loadData()
        setCountryData(dataService.current.getCountryData())

        const terrRes = await fetch('/data/territories.json')
        if (terrRes.ok) {
          const terrData = await terrRes.json()
          setTerritories(terrData.features ?? [])
        }
      } catch (err) {
        setError('Failed to load map data')
        console.error(err)
      } finally {
        setIsLoading(false)
      }
    }

    loadData()
  }, [setIsLoading, setError])

  // Debug effect to monitor state changes
  useEffect(() => {
    console.log('Selected countries changed:', selectedCountries)
    console.log('Distance info changed:', distanceInfo)
  }, [selectedCountries, distanceInfo])

  const handleCountryClick = (countryName: string) => {
    console.log('=== Country Click Event ===')
    console.log('Country clicked:', countryName)
    console.log('Current selection before:', selectedCountries)
    console.log('Selection length:', selectedCountries.length)
    
    if (selectCountriesRef.current.includes(countryName)) {
      // Deselect if already selected
      console.log('Deselecting country:', countryName)
      setSelectedCountries(prev => prev.filter(c => c !== countryName))
      setDistanceInfo(null)
      return
    }

    if (selectCountriesRef.current.length === 0) {
      // First country selection
      console.log('First country selection:', countryName)
      setSelectedCountries([countryName])
      setDistanceInfo(null)
    } else if (selectCountriesRef.current.length === 1) {
      // Second country selection - calculate distance
      console.log('Second country selection:', countryName)
      console.log('Will search distance between:', selectCountriesRef.current[0], 'and', countryName)
      const newSelection = [selectCountriesRef.current[0], countryName]
      console.log('New selection will be:', newSelection)
      setSelectedCountries(newSelection)
      
      // Debug: Check what distances are available
      const allDistances = dataService.current.getCountryDistances()
      console.log('Total distances in database:', allDistances.length)
      console.log('Sample distances:', allDistances.slice(0, 5))
      
      // Check if our countries exist in the distance data
      const fromMatches = allDistances.filter(d => d.fromCountry === newSelection[0] || d.toCountry === newSelection[0])
      const toMatches = allDistances.filter(d => d.fromCountry === newSelection[1] || d.toCountry === newSelection[1])
      console.log(`Distances involving ${newSelection[0]}:`, fromMatches.length)
      console.log(`Distances involving ${newSelection[1]}:`, toMatches.length)
      
      const distance = dataService.current.findDistanceBetweenCountries(
        newSelection[0],
        newSelection[1]
      )
      
      if (distance) {
        setDistanceInfo(distance)
        console.log('Distance found and set:', distance)
      } else {
        console.log('No distance data found for:', newSelection[0], 'to', newSelection[1])
        // Keep the selection but show a message
        setDistanceInfo(null)
      }
    } else {
      // Replace the second country with the newly clicked one
      console.log('Replacing second country with:', countryName)
      console.log('Will search distance between:', selectCountriesRef.current[0], 'and', countryName)
      const newSelection = [selectCountriesRef.current[0], countryName]
      console.log('New selection will be:', newSelection)
      setSelectedCountries(newSelection)
      
      // Calculate distance for the new pair
      const distance = dataService.current.findDistanceBetweenCountries(
        newSelection[0],
        newSelection[1]
      )
      
      if (distance) {
        setDistanceInfo(distance)
        console.log('Distance found and set:', distance)
      } else {
        console.log('No distance data found for:', newSelection[0], 'to', newSelection[1])
        setDistanceInfo(null)
      }
    }
    console.log('=== End Country Click Event ===')
  }

  const onEachFeature = (feature: any, layer: any) => {
    const countryName = feature.properties?.NAME
    
    // Validate country name - should be a valid name (not empty)
    const isValidCountryName = countryName && 
      typeof countryName === 'string' && 
      countryName.trim() !== ''
    
    if (isValidCountryName) {
      layer.on({
        click: (e: any) => {
          e.originalEvent.stopPropagation()
          console.log('Valid country clicked:', countryName)
          handleCountryClick(countryName)
        },
        mouseover: (e: any) => {
          if (!selectedCountries.includes(countryName)) {
            e.target.setStyle({
              weight: 3,
              fillOpacity: 0.8
            })
          }
        },
        mouseout: (e: any) => {
          const style = getCountryStyle({ properties: { NAME: countryName } }, selectedCountries)
          e.target.setStyle(style)
        }
      })
      
      layer.bindTooltip(countryName, {
        permanent: false,
        direction: 'center',
        className: 'country-tooltip'
      })
    } else {
      console.log('Invalid country name found:', countryName)
    }
  }

  const getDistanceLine = (): LatLngExpression[] | null => {
    console.log('Getting distance line, distanceInfo:', distanceInfo)
    if (!distanceInfo) {
      console.log('No distance info, returning null')
      return null
    }
    
    const line: LatLngExpression[] = [
      [distanceInfo.fromPointLat, distanceInfo.fromPointLon] as [number, number],
      [distanceInfo.toPointLat, distanceInfo.toPointLon] as [number, number]
    ]
    console.log('Distance line calculated:', line)
    return line
  }

  // Get the border-to-border distance line
  const distanceLine = getDistanceLine()
  console.log('Distance line for rendering:', distanceLine)

  const getCentroids = () => {
    if (!distanceInfo || selectedCountries.length !== 2) {
      return []
    }
    
    return [
      {
        position: [distanceInfo.fromCenterLat, distanceInfo.fromCenterLon] as [number, number],
        country: distanceInfo.fromCountry
      },
      {
        position: [distanceInfo.toCenterLat, distanceInfo.toCenterLon] as [number, number],
        country: distanceInfo.toCountry
      }
    ]
  }

  if (isLoading) {
    return (
      <div className={cn('flex items-center justify-center h-full', className)}>
        <div className="text-lg">Loading map data...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={cn('flex items-center justify-center h-full', className)}>
        <div className="text-lg text-red-500">{error}</div>
      </div>
    )
  }

  if (!countryData) {
    return (
      <div className={cn('flex items-center justify-center h-full', className)}>
        <div className="text-lg">No map data available</div>
      </div>
    )
  }

  return (
    <div className={cn('relative h-full w-full', className)}>
      <MapContainer
        center={[20, 0] as LatLngExpression}
        zoom={2}
        className="h-full w-full"
        zoomControl={true}
        scrollWheelZoom={true}
        worldCopyJump={false}
        maxBounds={[[-90, -180], [90, 180]]}
        maxBoundsViscosity={1}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          noWrap={true}
        />
        
        <GeoJSON
          key={`countries-geojson-${selectedCountries.join('-')}`}
          data={countryData}
          style={feature => getCountryStyle(feature, selectedCountries)}
          onEachFeature={onEachFeature}
          pointToLayer={(feature, latlng) => {
            const selected = selectedCountries.includes(feature.properties?.NAME)
            const marker = L.circleMarker(latlng, {
              radius: 6,
              fillColor: selected ? '#3b82f6' : '#e5e7eb',
              color: selected ? '#1d4ed8' : '#9ca3af',
              weight: selected ? 2 : 1,
              opacity: 1,
              fillOpacity: selected ? 0.8 : 0.7,
            })
            let boundMap: L.Map | null = null
            const onZoomEnd = () => {
              if (!boundMap) return
              const metersPerPixel =
                (156543.03392 * Math.cos((latlng.lat * Math.PI) / 180)) /
                Math.pow(2, boundMap.getZoom())
              const kmPixels = 25000 / metersPerPixel
              marker.setRadius(Math.max(6, kmPixels))
            }
            marker.on('add', (e) => {
              boundMap = (e.target as L.CircleMarker & { _map: L.Map })._map
              onZoomEnd()
              boundMap.on('zoomend', onZoomEnd)
            })
            marker.on('remove', () => {
              if (boundMap) boundMap.off('zoomend', onZoomEnd)
              boundMap = null
            })
            return marker
          }}
        />

        {/* Territory polygons - shown grey when parent country is selected */}
        {territories
          .filter(t => {
            const sov = t.properties?.SOVEREIGNT
            if (!sov) return false
            return selectedCountries.some(sel => {
              if (sel === sov) return true
              // Check if selected is a region of this territory's sovereign
              const feature = countryData?.features.find(f => f.properties?.NAME === sel)
              return feature?.properties?.PARENT_COUNTRY === sov
            })
          })
          .map((t, i) => (
            <GeoJSON
              key={`territory-${t.properties.NAME}-${i}-${selectedCountries.join('-')}`}
              data={t}
              style={{
                fillColor: '#9ca3af',
                weight: 1,
                opacity: 0.6,
                color: '#6b7280',
                fillOpacity: 0.25,
                dashArray: '3 5',
              }}
              onEachFeature={(feature: any, layer: any) => {
                layer.bindTooltip(
                  `${feature.properties?.NAME} (territory)`,
                  { permanent: false, direction: 'center', className: 'country-tooltip' }
                )
              }}
            />
          ))
        }
        
        {/* V1: centroid-to-centroid path, V2: border-to-border path */}
        {distanceInfo && selectedCountries.length === 2 && (
          <GeodesicLine
            key={`line-${distanceMode}-${selectedCountries.join('-')}`}
            positions={
              distanceMode === 'v1'
                ? [
                    [distanceInfo.fromCenterLat, distanceInfo.fromCenterLon],
                    [distanceInfo.toCenterLat, distanceInfo.toCenterLon]
                  ]
                : [
                    [distanceInfo.fromPointLat, distanceInfo.fromPointLon],
                    [distanceInfo.toPointLat, distanceInfo.toPointLon]
                  ]
            }
            pathOptions={{
              color: "#ef4444",
              weight: 7,
              opacity: 0.8
            }}
          >
            <div className="p-2">
              <div className="font-semibold">
                {distanceMode === 'v1' ? 'Centroid Path' : 'Shortest Border Path'}
              </div>
              <div>
                {distanceMode === 'v1'
                  ? `Centroid-to-centroid distance`
                  : `Border-to-border distance: ${formatDistance(distanceInfo.distanceKm)}`}
              </div>
              <div className="text-xs text-gray-600">
                From: {distanceInfo.fromCountry}<br/>
                To: {distanceInfo.toCountry}
              </div>
            </div>
          </GeodesicLine>
        )}
        
        <Pane name="centroidsPane">
          {/* Centroid Markers */}
          {getCentroids().map((center) => (
            <CircleMarker
              key={`center-${center.country}`}
              center={center.position}
              radius={8}
              pathOptions={{
                fillColor: "#fbbf24",
                color: "#f59e0b",
                weight: 2,
                opacity: 1,
                fillOpacity: 0.8
              }}
            >
              <Popup>
                <div className="p-2">
                  <div className="font-semibold">Centroid</div>
                  <div>{center.country}</div>
                  <div className="text-xs text-gray-600">
                    {center.position[0].toFixed(4)}°, {center.position[1].toFixed(4)}°
                  </div>
                </div>
              </Popup>
            </CircleMarker>
          ))}
          
          {/* Direction Arrow — V1: initial bearing, V2: rhumb line bearing */}
          {distanceInfo && selectedCountries.length === 2 && (() => {
            const arrowBearing = distanceMode === 'v1'
              ? initialBearing(
                  distanceInfo.fromCenterLat, distanceInfo.fromCenterLon,
                  distanceInfo.toCenterLat, distanceInfo.toCenterLon
                )
              : (distanceInfo.rhumbLineBearing ?? 0)
            const arrowLabel = distanceMode === 'v1' ? 'Initial Bearing' : 'Rhumb Line'
            return (
              <Marker
                key={`arrow-${distanceMode}`}
                position={[distanceInfo.fromCenterLat, distanceInfo.fromCenterLon]}
                icon={L.divIcon({
                  html: `<div style="transform: rotate(${arrowBearing}deg); width: 24px; height: 24px; display: flex; align-items: center; justify-content: center;">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2L22 12L16 12L16 22L8 22L8 12L2 12L12 2Z" fill="#dc2626" stroke="#991b1b" stroke-width="1"/>
                    </svg>
                  </div>`,
                  className: 'loxodrome-arrow',
                  iconSize: [24, 24],
                  iconAnchor: [12, 12]
                })}
              >
                <Popup>
                  <div className="p-2">
                    <div className="font-semibold">{arrowLabel} Direction</div>
                    <div>From: {distanceInfo.fromCountry}</div>
                    <div>Bearing: {arrowBearing.toFixed(1)}°</div>
                    <div className="text-xs text-gray-600">
                      {distanceMode === 'v1'
                        ? 'Great circle initial bearing from centroid'
                        : 'Constant bearing path (rhumb line)'}
                    </div>
                  </div>
                </Popup>
              </Marker>
            )
          })()}
          </Pane>
      </MapContainer>
      
      {/* Info Panel */}
      <div className="z-[9999] absolute bottom-2 left-2 right-2 sm:left-auto sm:bottom-4 sm:right-4 sm:w-80 bg-white/90 backdrop-blur-sm p-4 pt-2 rounded-lg shadow-lg">
        {/* Version Toggle */}
        <div className="flex items-center justify-center mb-2">
          <div className="flex rounded-md border border-gray-300 overflow-hidden text-sm font-medium">
            <button
              className={cn(
                'px-3 py-1 transition-colors',
                distanceMode === 'v1'
                  ? 'bg-gray-900 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-100'
              )}
              onClick={() => setDistanceMode('v1')}
            >
              V1
            </button>
            <button
              className={cn(
                'px-3 py-1 transition-colors',
                distanceMode === 'v2'
                  ? 'bg-gray-900 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-100'
              )}
              onClick={() => setDistanceMode('v2')}
            >
              V2
            </button>
          </div>
        </div>

        {selectedCountries.length < 2 && (<>
        <h3 className="font-semibold mb-2 pt-1 text-lg">Country Distance Viewer</h3>
        <p className="text-sm text-gray-600">
          Click on two countries to see the distance and direction between them.
        </p></>)}

        {selectedCountries.length > 0 && (
          <div className="space-y-2">
            {selectedCountries.length === 2 && (
              distanceInfo ? (
                <DistanceInfoPanel
                  distanceInfo={distanceInfo}
                  mode={distanceMode}
                  enclaveRelation={dataService.current.getEnclaveRelation(
                    distanceInfo.fromCountry,
                    distanceInfo.toCountry,
                  )}
                />
              ) : (
                <div className="text-sm p-2 bg-yellow-50 rounded border border-yellow-200">
                  <div className="text-yellow-800">No distance data available for this country pair.</div>
                </div>
              )
            )}

            <button
              onClick={() => {
                setSelectedCountries([])
                setDistanceInfo(null)
              }}
              className="text-sm text-white bg-gray-900 hover:bg-gray-800 px-2 py-1 w-full h-8 rounded-md"
            >
              Clear Selection
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

function DistanceInfoPanel({
  distanceInfo,
  mode,
  enclaveRelation,
}: {
  distanceInfo: CountryDistance
  mode: 'v1' | 'v2'
  enclaveRelation: 'inside' | 'surrounds' | null
}) {
  const distanceLabel = (km: number): string => {
    if (km === 0 && enclaveRelation) {
      return enclaveRelation === 'inside' ? 'Inside 🔥🔥🔥' : 'Surrounding 🔥🔥🔥'
    }
    return distanceToWarmthText(km)
  }
  const v1Distance = haversineDistance(
    distanceInfo.fromCenterLat, distanceInfo.fromCenterLon,
    distanceInfo.toCenterLat, distanceInfo.toCenterLon
  )
  const v1Bearing = initialBearing(
    distanceInfo.fromCenterLat, distanceInfo.fromCenterLon,
    distanceInfo.toCenterLat, distanceInfo.toCenterLon
  )

  const displayDistance = mode === 'v1' ? v1Distance : distanceInfo.distanceKm
  const displayBearing = mode === 'v1' ? v1Bearing : (distanceInfo.rhumbLineBearing ?? 0)
  const altDistance = mode === 'v1' ? distanceInfo.distanceKm : v1Distance
  const altBearing = mode === 'v1' ? (distanceInfo.rhumbLineBearing ?? 0) : v1Bearing

  return (
    <div className="text-sm space-y-2 rounded w-full">
      <div className="w-full flex flex-col items-center text-lg">
        <div>
          <strong>{distanceInfo.fromCountry}</strong> → <strong>{distanceInfo.toCountry}</strong>
        </div>
      </div>
      <div className="w-full flex flex-row justify-between">
        <div>Distance:</div>
        <div className="w-full flex flex-row justify-between pl-1">
          <strong>{distanceLabel(displayDistance)}</strong>
          {displayDistance !== 0 && (
            <div className="text-xs text-gray-500">({formatDistance(displayDistance)})</div>
          )}
        </div>
      </div>
      <div className="w-full flex flex-row justify-between">
        <div>Direction:</div>
        <div className="w-full flex flex-row justify-between pl-1">
          <strong>{bearingToCardinal(displayBearing)}</strong>
          <div className="text-xs text-gray-500">({displayBearing.toFixed(1)}°)</div>
        </div>
      </div>

      {/* Comparison line showing the other version */}
      <div className="border-t pt-1.5 mt-1">
        <div className="text-[11px] text-gray-400">
          {mode === 'v1' ? 'V2' : 'V1'}:{' '}
          <span className="text-gray-500">{distanceLabel(altDistance)}</span>
          {altDistance !== 0 && (
            <span className="text-gray-400"> ({formatDistance(altDistance)})</span>
          )}
          {' · '}
          <span className="text-gray-500">{bearingToCardinal(altBearing)}</span>
          <span className="text-gray-400"> ({altBearing.toFixed(1)}°)</span>
        </div>
      </div>

      <div className="hidden sm:block border-t pt-2 mt-2 rounded-sm">
        <div className="text-xs text-gray-700 font-medium mb-1">Map Legend:</div>
        <div className="text-xs space-y-1 flex flex-row space-x-4 pl-2">
          <div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-amber-400 rounded-full border border-amber-500"></div>
              <span>Centroids</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-3 flex items-center justify-center">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2L22 12L12 22L12 16L2 16L2 8L12 8L12 2Z" fill="#dc2626" stroke="#991b1b" strokeWidth="1"/>
                </svg>
              </div>
              <span>{mode === 'v1' ? 'V1 bearing direction' : 'Rhumbline direction'}</span>
            </div>
          </div>
          <div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-1.5 bg-red-500"></div>
              <span>{mode === 'v1' ? 'Centroid path' : 'Shortest border path'}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const getCountryStyle = (feature: any, selectedCountries: string[]) => {
  const countryName = feature?.properties?.NAME
  if (countryName && selectedCountries.includes(countryName)) {
    return {
      fillColor: '#3b82f6',
      weight: 2,
      opacity: 1,
      color: '#1d4ed8',
      fillOpacity: 0.7
    }
  }
  return {
    fillColor: '#e5e7eb',
    weight: 1,
    opacity: 1,
    color: '#9ca3af',
    fillOpacity: 0.5
  }
}